<script>
const MILLISECONDS_SECOND = 1000
const MILLISECONDS_MINUTE = 60 * MILLISECONDS_SECOND
const MILLISECONDS_HOUR = 60 * MILLISECONDS_MINUTE
const MILLISECONDS_DAY = 24 * MILLISECONDS_HOUR

export default {
  name: 'countdown',

  data() {
    return {
      counting: false,
      totalMilliseconds: 0,
      endAt: null,
      timeout: null,
      visibilityHandler: null,
    }
  },

  props: {
    autoStart: {
      type: Boolean,
      default: true,
    },
    emitEvents: {
      type: Boolean,
      default: true,
    },
    interval: {
      type: Number,
      default: 1000,
      validator: value => value >= 0,
    },
    tag: {
      type: String,
      default: 'span',
    },
    time: {
      type: Number,
      default: 0,
      validator: value => value >= 0,
    },
    transform: {
      type: Function,
      default: props => props,
    },
  },

  computed: {
    days() {
      return Math.floor(this.totalMilliseconds / MILLISECONDS_DAY)
    },
    hours() {
      return Math.floor(
        (this.totalMilliseconds % MILLISECONDS_DAY) / MILLISECONDS_HOUR
      )
    },
    minutes() {
      return Math.floor(
        (this.totalMilliseconds % MILLISECONDS_HOUR) / MILLISECONDS_MINUTE
      )
    },
    seconds() {
      return Math.floor(
        (this.totalMilliseconds % MILLISECONDS_MINUTE) / MILLISECONDS_SECOND
      )
    },
    milliseconds() {
      return Math.floor(this.totalMilliseconds % MILLISECONDS_SECOND)
    },
    totalDays() {
      return this.days
    },
    totalHours() {
      return Math.floor(this.totalMilliseconds / MILLISECONDS_HOUR)
    },
    totalMinutes() {
      return Math.floor(this.totalMilliseconds / MILLISECONDS_MINUTE)
    },
    totalSeconds() {
      return Math.floor(this.totalMilliseconds / MILLISECONDS_SECOND)
    },
    normalizedTime() {
      return Math.max(Number(this.time) || 0, 0)
    },
  },

  render(createElement) {
    return createElement(
      this.tag,
      this.$scopedSlots.default
        ? [
            this.$scopedSlots.default(
              this.transform({
                days: this.days,
                hours: this.hours,
                minutes: this.minutes,
                seconds: this.seconds,
                milliseconds: this.milliseconds,
                totalDays: this.totalDays,
                totalHours: this.totalHours,
                totalMinutes: this.totalMinutes,
                totalSeconds: this.totalSeconds,
                totalMilliseconds: this.totalMilliseconds,
              })
            ),
          ]
        : this.$slots.default
    )
  },

  watch: {
    time: {
      immediate: true,
      handler() {
        this.reset()

        if (this.autoStart && this.normalizedTime > 0) {
          this.$nextTick(() => {
            this.start()
          })
        }
      },
    },
  },

  mounted() {
    this.visibilityHandler = () => {
      if (document.visibilityState !== 'visible') {
        return
      }

      if (this.counting) {
        this.sync()
        this.pause()
        this.continue()
        return
      }

      if (this.totalMilliseconds > 0) {
        this.start()
      }
    }

    document.addEventListener('visibilitychange', this.visibilityHandler)

    // if (this.autoStart) {
    //   this.$nextTick(() => {
    //     this.start()
    //   })
    // }
  },

  methods: {
    reset() {
      this.pause()
      this.counting = false
      this.totalMilliseconds = this.normalizedTime
      this.endAt = null
    },

    start() {
      if (this.counting) {
        return
      }

      this.totalMilliseconds = Math.max(this.totalMilliseconds, 0)

      if (this.totalMilliseconds <= 0) {
        this.end()
        return
      }

      this.counting = true
      this.endAt = Date.now() + this.totalMilliseconds

      if (this.emitEvents) {
        this.$emit('start')
      }

      this.continue()
    },

    continue() {
      if (!this.counting) {
        return
      }

      this.sync()

      const delay = Math.min(this.totalMilliseconds, this.interval)

      if (delay > 0) {
        this.timeout = setTimeout(this.progress, delay)
      } else {
        this.end()
      }
    },

    pause() {
      clearTimeout(this.timeout)
      this.timeout = null
    },

    sync() {
      if (!this.endAt) {
        return
      }

      this.totalMilliseconds = Math.max(this.endAt - Date.now(), 0)
    },

    progress() {
      if (!this.counting) {
        return
      }

      this.sync()

      if (this.emitEvents && this.totalMilliseconds > 0) {
        this.$emit('progress', {
          days: this.days,
          hours: this.hours,
          minutes: this.minutes,
          seconds: this.seconds,
          totalDays: this.totalDays,
          totalHours: this.totalHours,
          totalMinutes: this.totalMinutes,
          totalSeconds: this.totalSeconds,
        })
      }

      this.continue()
    },

    abort() {
      if (!this.counting) {
        return
      }

      this.pause()
      this.counting = false
      this.endAt = null

      if (this.emitEvents) {
        this.$emit('abort')
      }
    },

    end() {
      if (!this.counting) {
        return
      }

      this.pause()
      this.totalMilliseconds = 0
      this.counting = false
      this.endAt = null

      if (this.emitEvents) {
        this.$emit('end')
      }
    },
  },

  beforeDestroy() {
    this.pause()

    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
    }
  },
}
</script>